import type { DriverNames, TimestampInMillis } from '@cobliteam/common-types';
import moment from 'moment';
import { identificationTokenGraphQLV2ToIdentificationTokenV2 } from '../..';
import type { AddDriverGraphQLAPI, DriverAPI, DriverGraphQLAPI, SaveDriverAPIRequest, SaveDriverGraphQLAPI } from './APITypes';
import type { Driver, NewDriver } from './Types';

const dateTimeStrToMilli = (date: string | null): TimestampInMillis | undefined => {
  return (date && moment(date, 'YYYY-MM-DD HH:mm:ss ZZ').valueOf()) || undefined;
};

const dateStrToMilli = (date?: string): TimestampInMillis | undefined => {
  return (date && moment(date, 'YYYY-MM-DD').valueOf()) || undefined;
};

const millisToDateString = (millis?: TimestampInMillis): string | undefined => {
  return (millis && moment(millis).format('YYYY-MM-DD')) || undefined;
};

export const driverAPIToDriver = (driver: DriverAPI): Driver => {
  return {
    fleetId: driver.fleet_id,
    id: driver.id,
    name: driver.name,
    assigned: driver.assigned,
    phoneNumbers: driver.phone_numbers,
    firstLicenseAcquirementDateInMillis: dateTimeStrToMilli(driver.first_license_acquirement_date),
    licenseExpirationDateInMillis: dateTimeStrToMilli(driver.license_expiration_date),
    licenseCategory: driver.license_category || undefined,
    driverCode: driver.driver_code,
    identifier: driver.identifier || undefined,
  };
};

export const saveDriverParseToAPI = (driver: Driver | NewDriver): SaveDriverAPIRequest => {
  return {
    id: ('id' in driver && driver.id) || undefined,
    name: driver.name,
    phone_numbers: driver.phoneNumbers,
    license_category: driver.licenseCategory || null,
    driver_code: driver.driverCode || undefined,
    license_expiration_date: driver.licenseExpirationDateInMillis || null,
    first_license_acquirement_date: driver.firstLicenseAcquirementDateInMillis || null,
  };
};

export const driverGraphQLAPIToDriver = (graphQLDriver: DriverGraphQLAPI): Driver => {
  return {
    fleetId: graphQLDriver?.fleet_id ?? undefined,
    id: graphQLDriver.id,
    name: graphQLDriver.name,
    assigned: graphQLDriver.assigned,
    phoneNumbers: [graphQLDriver.phone_number || ''],
    firstLicenseAcquirementDateInMillis: dateStrToMilli(graphQLDriver.first_license_acquirement_date),
    licenseExpirationDateInMillis: dateStrToMilli(graphQLDriver.license_expiration_date),
    licenseCategory: graphQLDriver.license_category || undefined,
    driverCode: graphQLDriver.driver_code,
    identifier: graphQLDriver.identifier?.toUpperCase() || undefined,
    identificationToken: graphQLDriver.identification_token ? identificationTokenGraphQLV2ToIdentificationTokenV2(graphQLDriver.identification_token) : undefined,
    cpf: graphQLDriver.cpf,
    driverLicense: graphQLDriver.driver_license,
    registryId: graphQLDriver.registry_id,
    vehicle: graphQLDriver.vehicle
      ? {
          ...graphQLDriver.vehicle,
          deviceId: (graphQLDriver.vehicle as { device?: { id?: string } }).device?.id ?? graphQLDriver.vehicle.deviceId,
        }
      : undefined,
  };
};

const cleanPhoneNumber = (string: string) => {
  return string.replace(/[^a-zA-Z0-9]/g, '');
};

export const newDriverToAddDriverGraphQLAPI = (driver: NewDriver): AddDriverGraphQLAPI => {
  const phone = driver.phoneNumbers?.length && driver.phoneNumbers[0] ? cleanPhoneNumber(driver.phoneNumbers[0]) : undefined;

  return {
    name: driver.name,
    phone_number: phone,
    first_license_acquirement_date: millisToDateString(driver.firstLicenseAcquirementDateInMillis),
    license_expiration_date: millisToDateString(driver.licenseExpirationDateInMillis),
    license_category: driver.licenseCategory,
    cpf: driver.cpf,
    driver_license: driver.driverLicense,
    registry_id: driver.registryId,
  };
};

export const driverToSaveDriverGraphQLAPI = (driver: Driver): SaveDriverGraphQLAPI => {
  return {
    id: driver.id,
    ...newDriverToAddDriverGraphQLAPI(driver),
  };
};

export const driverGraphQLAPIToDriverNames = (graphQLDrivers: DriverGraphQLAPI[]): DriverNames =>
  graphQLDrivers.reduce((acc, value) => {
    acc[value.id] = value.name;
    return acc;
  }, {} as DriverNames);
